import { Module } from 'vuex';
import * as Sentry from '@sentry/browser';

import {
  ActiveExperimentGetter,
  ClassroomIntentKeyForSignup,
  PrivacyProperties,
  FeatureSwitches,
  GlobalState,
  RootState,
  User,
  StreakStats,
  Optional,
} from 'frontend/types';

import { inExperimentGetter } from 'frontend/utils/experiment';
import { updateProfileFlag } from 'frontend/utils/flags';
import { Discount } from 'frontend/types/premium';

const globalInfo: Module<GlobalState, RootState> = {
  namespaced: true,
  state: () => ({
    analytics: undefined,
    experiments: undefined,
    feature_switches: undefined,
    user: undefined,
    privacy_properties: undefined,
    join_classroom_intent_key_for_signup: undefined,
    loaded_profitwell_snippet: undefined,
    posted_profitwell_user_email: undefined,
    google_oauth_client_id: undefined,
    discount: undefined,
    front_token_campgrounds_authors: undefined,
    algolia_proxy_url: undefined,
  }),
  mutations: {
    setValue(state: GlobalState, value: GlobalState): void {
      Object.assign(state, value);

      // Add user debug info for Sentry
      if (state.user) {
        Sentry.setTags({
          anonymous: state.user.is_authenticated === true,
          b2: state.user.has_brilliant_premium === true,
        });
        if (state.user.is_authenticated) {
          Sentry.setUser({
            id: `${state.user.user_id}`,
          });
        }
      }
    },
    clearTrackedEvents(state: GlobalState) {
      if (state.analytics) {
        state.analytics.javascript_tracked_events = [];
      }
    },
    setJoinClassroomIntentKeyForSignup(
      state: GlobalState,
      joinClassroomIntentKeyForSignup: ClassroomIntentKeyForSignup | undefined,
    ) {
      state.join_classroom_intent_key_for_signup = joinClassroomIntentKeyForSignup;
    },
    closePrivacyBanner(state: GlobalState) {
      if (state.privacy_properties) {
        state.privacy_properties.viewed_privacy_banner = true;
      }
    },
    setPrivacyBannerHeight(state: GlobalState, height: number) {
      if (state.privacy_properties) {
        state.privacy_properties.height = height;
      }
    },
    toggleThirdPartyCookies(state: GlobalState, enabled: boolean) {
      if (state.privacy_properties) {
        state.privacy_properties.third_party_cookies_disabled = !enabled;
      }
    },
    loadProfitwellSnippet(state: GlobalState) {
      state.loaded_profitwell_snippet = true;
    },
    setProfitwellUserEmailAsPosted(state: GlobalState) {
      state.posted_profitwell_user_email = true;
    },
    setUser(state: GlobalState, user: User) {
      state.user = user;
    },
    setStreakData(state: GlobalState, streakData: StreakStats) {
      if (state.user) {
        state.user.streak = streakData;
      }
    },
  },
  actions: {
    clearTrackedEvents({ commit }) {
      commit('clearTrackedEvents');
    },
    setJoinClassroomIntentKeyForSignup(
      { commit },
      joinClassroomIntentKeyForSignup: ClassroomIntentKeyForSignup | undefined,
    ) {
      commit('setJoinClassroomIntentKeyForSignup', joinClassroomIntentKeyForSignup);
    },
    async closePrivacyBanner({ commit }) {
      await updateProfileFlag('viewed_privacy_banner');
      commit('closePrivacyBanner');
    },
    async toggleThirdPartyCookies({ commit }, enabled: boolean) {
      await updateProfileFlag('third_party_cookies_disabled', !enabled);
      commit('toggleThirdPartyCookies', enabled);
    },
    loadProfitwellSnippet({ commit }) {
      commit('loadProfitwellSnippet');
    },
    setProfitwellUserEmailAsPosted({ commit }) {
      commit('setProfitwellUserEmailAsPosted');
    },
    setUser({ commit }, user: User) {
      commit('setUser', user);
    },
    setStreakData({ commit }, streakData: StreakStats) {
      commit('setStreakData', streakData);
    },
  },
  getters: {
    inExperiment: state => inExperimentGetter(state.experiments),
    featureSwitches(state): FeatureSwitches | undefined {
      return state.feature_switches;
    },
    privacyProperties(state): PrivacyProperties | undefined {
      return state.privacy_properties;
    },
    privacyBannerHeight: (state: GlobalState) => state.privacy_properties?.height ?? 0,
    userFullName(state): string {
      return state.user ? `${state.user.first_name} ${state.user.last_name}` : '';
    },
    joinClassroomIntentKeyForSignup(state): ClassroomIntentKeyForSignup | undefined {
      return state.join_classroom_intent_key_for_signup;
    },
    isAuthenticated(state): boolean {
      return !!state.user && state.user.is_authenticated === true;
    },
    userType(state): string | null {
      return state.user?.user_type ?? null;
    },
    googleOauthClientId(state): string | undefined {
      return state.google_oauth_client_id;
    },
    frontTokenCampgroundsAuthors(state): string | undefined {
      return state.front_token_campgrounds_authors;
    },
    algoliaProxyUrl(state): string | undefined {
      return state.algolia_proxy_url;
    },
    getActiveTreatment:
      (state, getters): ActiveExperimentGetter =>
      (experimentName: string) => {
        const treatment =
          state.experiments && state.experiments[experimentName]
            ? state.experiments[experimentName].treatment_name
            : 'control';
        getters.inExperiment(experimentName, treatment); // to track the experiment
        return treatment;
      },
    streakData(state): StreakStats | undefined {
      return state.user?.streak;
    },
    discount(state): Optional<Discount> {
      return state.discount;
    },
  },
};

export default globalInfo;
